import { createApi } from '@reduxjs/toolkit/query/react';

import { axiosBaseQuery } from './axios-base-query';

import type {
  AssuranceOperation,
  AssuranceProviderId,
  AssuranceTestResults,
  AssuranceUploadResponse,
} from '~/shared/types/assurance-tests';

interface AssuranceTarget {
  providerId: AssuranceProviderId;
  projectId: string;
  assessmentId: string;
}

export interface AssuranceOperationRequest extends AssuranceTarget {
  operation: AssuranceOperation;
  file: File;
  config?: unknown;
}

export interface AssuranceUploadRequest {
  projectId: string;
  assessmentId: string;
  testResults: AssuranceTestResults;
}

const enc = encodeURIComponent;

export const assuranceTestsApi = createApi({
  reducerPath: 'assuranceTestsApi',
  baseQuery: axiosBaseQuery({ baseUrl: '/api/assurance' }),
  endpoints: (builder) => ({
    // One endpoint for every provider; the response shape is the provider's own.
    assuranceOperation: builder.mutation<unknown, AssuranceOperationRequest>({
      query: ({
        providerId,
        projectId,
        assessmentId,
        operation,
        file,
        config,
      }) => {
        const data = new FormData();
        data.append('file', file);
        if (config !== undefined) {
          data.append('config', JSON.stringify(config));
        }
        return {
          url: `/${enc(providerId)}/projects/${enc(projectId)}/assessments/${enc(assessmentId)}/${enc(operation)}`,
          method: 'POST',
          data,
        };
      },
    }),
    uploadAssuranceTestResults: builder.mutation<
      AssuranceUploadResponse,
      AssuranceUploadRequest
    >({
      query: ({ projectId, assessmentId, testResults }) => ({
        url: `/projects/${enc(projectId)}/assessments/${enc(assessmentId)}/test-results`,
        method: 'POST',
        data: { testResults },
      }),
    }),
  }),
});

export const {
  useAssuranceOperationMutation,
  useUploadAssuranceTestResultsMutation,
} = assuranceTestsApi;

// The server's refusal sentence when it sent one, else the fallback.
export function assuranceErrorMessage(
  error: unknown,
  fallback: string
): string {
  const message = (error as { data?: { message?: unknown } } | undefined)?.data
    ?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
}
